import { useState, useRef, useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ratingsService } from '../services/ratingsService';
import type { StockRating, RatingAction } from '../types/ratings';

interface UpgradesDowngradesWidgetProps {
  onRemove: () => void;
  ticker?: string;
}

// Grade abbreviations for long grade names
const abbreviateGrade = (grade: string | null): string => {
  if (!grade) return '-';

  const abbreviations: Record<string, string> = {
    'Overweight': 'OVERW',
    'Underweight': 'UNDERW',
    'Outperform': 'OUTPERF',
    'Underperform': 'UNDERPERF',
    'Market Perform': 'MKT PERF',
    'Sector Perform': 'SEC PERF',
    'Equal Weight': 'EQ WEIGHT',
    'Equal-Weight': 'EQ WEIGHT',
    'Sector Weight': 'SEC WEIGHT',
    'Strong Buy': 'STR BUY',
    'Strong Sell': 'STR SELL',
    'Accumulate': 'ACCUM',
    'Moderate Buy': 'MOD BUY',
    'Moderate Sell': 'MOD SELL',
    'Positive': 'POS',
    'Negative': 'NEG',
    'Neutral': 'NEUT',
    'In-Line': 'IN-LINE',
    'Inline': 'INLINE',
  };

  return abbreviations[grade] || grade.toUpperCase().slice(0, 8);
};

const formatPrice = (price: number | null): string => {
  if (price === null || price === undefined) return '-';
  return `$${price.toFixed(2)}`;
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '-';
  // Handle both date-only ("2026-03-18") and datetime ("2026-03-18 21:50:19")
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
    ? dateStr + 'T00:00:00'
    : dateStr.replace(' ', 'T');
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

interface RatingsTableProps {
  action: RatingAction;
  search: string;
  title: string;
  isUpgrade: boolean;
}

function RatingsTable({ action, search, title, isUpgrade }: RatingsTableProps) {
  const accentColor = isUpgrade ? 'bull-blue' : 'bear-red';
  const sentinelRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['stockRatings', action, search],
      queryFn: ({ pageParam }) =>
        ratingsService.fetchRatings({
          page: pageParam as number,
          perPage: 20,
          action,
          search: search || undefined,
        }),
      getNextPageParam: (lastPage) =>
        lastPage.page < lastPage.total_pages ? lastPage.page + 1 : undefined,
      initialPageParam: 1,
      staleTime: 5 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    });

  const allItems = data?.pages.flatMap((p) => p.results) ?? [];
  const totalCount = data?.pages[0]?.count ?? 0;

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
      {/* Panel header */}
      <div className={`px-3 py-2 border-b border-dark-border bg-${accentColor}/10`}>
        <h4 className={`text-sm font-semibold text-${accentColor}`}>
          {title} {totalCount > 0 && `(${totalCount})`}
        </h4>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-auto">
        {isLoading && (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            Loading...
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center h-full text-bear-red text-sm p-2">
            Error loading
          </div>
        )}

        {allItems.length > 0 && (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-dark-surface-light border-b border-dark-border">
              <tr>
                <th className="px-2 py-1.5 text-left font-semibold text-gray-300">Symbol</th>
                <th className="px-2 py-1.5 text-left font-semibold text-gray-300">Date</th>
                <th className="px-2 py-1.5 text-left font-semibold text-gray-300">Firm</th>
                <th className="px-2 py-1.5 text-left font-semibold text-gray-300">Rating</th>
                <th className="px-2 py-1.5 text-right font-semibold text-gray-300">Price</th>
              </tr>
            </thead>
            <tbody>
              {allItems.map((rating: StockRating, index: number) => (
                <tr
                  key={`${rating.ticker}-${rating.grading_company}-${index}`}
                  className="border-b border-dark-border hover:bg-dark-surface-light transition-colors"
                >
                  <td className="px-2 py-1.5">
                    {rating.news_url ? (
                      <a
                        href={rating.news_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`font-medium text-${accentColor} hover:underline`}
                      >
                        {rating.ticker}
                      </a>
                    ) : (
                      <span className={`font-medium text-${accentColor}`}>{rating.ticker}</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-gray-500">
                    {formatDate(rating.published_date)}
                  </td>
                  <td
                    className="px-2 py-1.5 text-gray-400 truncate max-w-[80px]"
                    title={rating.grading_company}
                  >
                    {rating.grading_company}
                  </td>
                  <td
                    className="px-2 py-1.5 text-gray-400"
                    title={`${rating.previous_grade || '-'} → ${rating.new_grade || '-'}`}
                  >
                    <span className="text-gray-500">{abbreviateGrade(rating.previous_grade)}</span>
                    <span className={`text-${accentColor} mx-1`}>→</span>
                    <span className={`text-${accentColor}`}>{abbreviateGrade(rating.new_grade)}</span>
                  </td>
                  <td className="px-2 py-1.5 text-right text-gray-300">
                    {formatPrice(rating.price_when_posted)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!isLoading && allItems.length === 0 && (
          <div className="flex items-center justify-center h-full text-gray-500 text-xs">
            {search ? 'No results' : 'None available'}
          </div>
        )}

        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} className="h-6 flex items-center justify-center">
          {isFetchingNextPage && (
            <span className="text-xs text-gray-600">Loading more...</span>
          )}
        </div>
      </div>
    </div>
  );
}

export function UpgradesDowngradesWidget({ onRemove, ticker }: UpgradesDowngradesWidgetProps) {
  const [search, setSearch] = useState(ticker?.toUpperCase() ?? '');
  const [searchInput, setSearchInput] = useState(ticker?.toUpperCase() ?? '');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  return (
    <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
      {/* Title Bar - drag handle */}
      <div className="widget-drag-handle flex items-center justify-between px-3 py-1.5 bg-dark-surface-light border-b border-dark-border cursor-move relative z-20">
        <h3 className="text-sm font-semibold text-white">Upgrades & Downgrades</h3>
        <button
          onClick={onRemove}
          className="text-gray-400 hover:text-white hover:bg-bear-red/20 text-sm px-2 py-0.5 rounded transition-colors"
          title="Close widget"
        >
          ×
        </button>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="px-3 py-2 border-b border-dark-border relative z-20">
        <div className="flex gap-2">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by ticker or firm..."
            className="flex-1 px-2 py-1 text-xs bg-dark-bg border border-dark-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-bull-blue"
          />
          <button
            type="submit"
            className="px-3 py-1 text-xs bg-dark-surface-light hover:bg-dark-border text-white rounded transition-colors"
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                setSearchInput('');
              }}
              className="px-2 py-1 text-xs text-gray-400 hover:text-white transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </form>

      {/* Side by side panels */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        <RatingsTable action="upgrade" search={search} title="Upgrades" isUpgrade={true} />
        <div className="w-px bg-dark-border" />
        <RatingsTable action="downgrade" search={search} title="Downgrades" isUpgrade={false} />
      </div>
    </div>
  );
}
